// Chained-leg aggregates math extracted from alpha-basket.ts. Owns
// slotReturnInWindow + the basket-level mean/median/hitRate/edge-vs-SPY
// computation per window (today, MTD, YTD, since-inception).

import { priceOnOrBefore, type DailyCache, type ScoresFile } from './alpha-zone-tracker';
import type { Slot, BasketAggregateRow, BasketAggregates, AlphaBasket, Leg } from './alpha-basket';
import { previousDay, priorMonthEnd, priorYearEnd } from './alpha-basket';

function allLegs(slot: Slot): Leg[] {
  return [...slot.history, slot.current];
}

/** Pick anchor price for a slot in [windowStart, windowEnd]:
 *  - If the open leg started in window → entryPrice
 *  - Otherwise → priceOnOrBefore(windowStart) for the symbol that was in
 *    that slot at windowStart (or earliest leg in window if cache lacks the date) */
export function slotReturnInWindow(
  slot: Slot,
  windowStart: string,
  windowEnd: string,
  currentPrice: number | null,
  cache: DailyCache | null,
): number | null {
  const legs = allLegs(slot);
  const inWindow = legs.filter((leg) => {
    const start = leg.entryDate;
    const end = leg.exitDate ?? windowEnd;
    return end >= windowStart && start <= windowEnd;
  });
  if (inWindow.length === 0) return null;

  let chained = 1;
  let any = false;
  const histories = cache?.histories ?? {};

  for (const leg of inWindow) {
    let anchor: number | null;
    if (leg.entryDate >= windowStart) {
      anchor = leg.entryPrice;
    } else {
      anchor = priceOnOrBefore(histories[leg.symbol], windowStart) ?? leg.entryPrice;
    }

    let terminal: number | null;
    if (leg.exitDate != null && leg.exitPrice != null && leg.exitDate <= windowEnd) {
      terminal = leg.exitPrice;
    } else if (leg.exitDate == null) {
      terminal = leg.entryDate <= windowEnd
        ? (currentPrice ?? priceOnOrBefore(histories[leg.symbol], windowEnd) ?? null)
        : null;
    } else {
      terminal = leg.exitPrice ?? null;
    }

    if (anchor == null || terminal == null || anchor <= 0) continue;
    chained *= terminal / anchor;
    any = true;
  }
  return any ? chained - 1 : null;
}

function median(xs: number[]): number | null {
  if (xs.length === 0) return null;
  const s = [...xs].sort((a, b) => a - b);
  const m = s.length;
  return m % 2 === 1 ? s[(m - 1) / 2] : (s[m / 2 - 1] + s[m / 2]) / 2;
}

function spyReturn(cache: DailyCache | null, windowStart: string, windowEnd: string): number | null {
  const hist = cache?.histories?.SPY;
  if (!hist) return null;
  const a = priceOnOrBefore(hist, windowStart);
  const b = priceOnOrBefore(hist, windowEnd);
  if (a == null || b == null || a <= 0) return null;
  return (b - a) / a;
}

function basketRow(
  slots: Slot[],
  windowStart: string,
  windowEnd: string,
  currentPriceBySym: Map<string, number>,
  cache: DailyCache | null,
): BasketAggregateRow {
  // Collect (weight, return) pairs. Weight defaults to 1/N when leg lacks a
  // weight field (preserves equal-weight semantics for legacy / unweighted baskets).
  const N = slots.length || 1;
  const defaultWeight = 1 / N;
  const pairs: Array<{ w: number; r: number }> = [];
  for (const slot of slots) {
    const cur = currentPriceBySym.get(slot.current.symbol) ?? null;
    const r = slotReturnInWindow(slot, windowStart, windowEnd, cur, cache);
    if (r != null && Number.isFinite(r)) {
      const w = slot.current.weight ?? defaultWeight;
      pairs.push({ w, r });
    }
  }
  // Weighted mean: sum(w_i * r_i) / sum(w_i). Renormalize over present slots so
  // missing legs (e.g., null prices) don't shrink the basket return artificially.
  const wSum = pairs.reduce((s, p) => s + p.w, 0);
  const mean = wSum > 0
    ? pairs.reduce((s, p) => s + p.w * p.r, 0) / wSum
    : null;
  // Median + hitRate stay equal-weighted on slot returns (they're robustness
  // diagnostics — knowing how many slots hit positive doesn't depend on weights).
  const slotReturns = pairs.map(p => p.r);
  const med = median(slotReturns);
  const hitRate = slotReturns.length > 0
    ? slotReturns.filter((r) => r > 0).length / slotReturns.length
    : null;
  const spy = spyReturn(cache, windowStart, windowEnd);
  const edge = mean != null && spy != null ? mean - spy : null;
  return { windowStart, mean, median: med, hitRate, spy, edgeVsSpy: edge };
}

export function computeAggregates(
  basket: AlphaBasket,
  scores: ScoresFile | null,
  cache: DailyCache | null,
  today: string,
): BasketAggregates {
  const currentPriceBySym = new Map<string, number>();
  for (const s of scores?.stocks ?? []) {
    if (typeof s.price === 'number' && Number.isFinite(s.price) && s.price > 0) {
      currentPriceBySym.set(s.symbol, s.price);
    }
  }
  return {
    asOf: today,
    today: basketRow(basket.slots, previousDay(today), today, currentPriceBySym, cache),
    mtd: basketRow(basket.slots, priorMonthEnd(today), today, currentPriceBySym, cache),
    ytd: basketRow(basket.slots, priorYearEnd(today), today, currentPriceBySym, cache),
    sinceInception: basketRow(basket.slots, basket.inception, today, currentPriceBySym, cache),
  };
}
